import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Chip } from '@cowoo/shared/ui-web';
import {
  ApiProblem,
  formatDate,
  formatDayShort,
  formatMoney,
  formatTime,
  qk,
  todayBerlin,
} from '@cowoo/shared';
import { api } from '../../lib/api';
import { useAdminStore } from '../../lib/store';
import { Panel, PanelRow } from '../../ui';
import { STATUS_TONE, objectLabel, whoLabel } from './bookingRow';
import { RebookDialog } from './RebookDialog';

/** § 9.2 — the four modes of the "Stornieren und erstatten" box. */
const MODES = ['refund_card', 'partial', 'cancel_only', 'credit'] as const;
type Mode = (typeof MODES)[number];

/** BR-PAY-07 — the default staff cap until Einstellungen says otherwise. */
const DEFAULT_STAFF_LIMIT_CENTS = 10_000;

/**
 * The booking detail panel (10-web-admin § 9.2).
 *
 * The refund box is the reason this screen exists, so it is the part that is
 * careful: the mode maps to the API's `mode` one to one, the amount is only
 * ever sent where the API takes one, and a mode staff cannot use is disabled
 * with the reason rather than hidden — someone has to know to hand it on.
 *
 * Check-in and check-out are overrides. The server refuses them for any day but
 * today (A-069: backdating presence would rewrite what the door events say),
 * and they are hidden here on other days rather than offered and refused.
 */
export function BookingPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const role = useAdminStore((state) => state.profile?.role);
  const [mode, setMode] = useState<Mode>('refund_card');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [rebooking, setRebooking] = useState(false);

  const booking = useQuery({
    queryKey: qk.admin.area('bookings', { id }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/bookings/{id}', { params: { path: { id } } });
      if (error) throw error;
      return data;
    },
  });

  const invalidate = () => {
    setProblem(null);
    setReason('');
    void queryClient.invalidateQueries({ queryKey: qk.admin.area('bookings') });
  };
  const onError = (error: unknown) => setProblem(error instanceof ApiProblem ? error : null);

  const cancel = useMutation({
    mutationFn: async () => {
      const cents = Math.round(Number(amount.replace(',', '.')) * 100);
      const { error } = await api.POST('/admin/bookings/{id}/cancel', {
        params: { path: { id } },
        body: {
          // "Teilerstattung" is a card refund with an amount (AF-13); the API
          // has three modes, not four.
          mode: mode === 'partial' ? 'refund_card' : mode,
          ...(mode === 'partial' && cents > 0 ? { amountCents: cents } : {}),
          reason,
        },
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError,
  });

  const note = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/bookings/{id}/notes', {
        params: { path: { id } },
        body: { note: reason.trim() },
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError,
  });

  const act = useMutation({
    mutationFn: async (what: 'checkin' | 'checkout' | 'resend-receipt') => {
      const path = { path: { id } } as const;
      const { error } =
        what === 'resend-receipt'
          ? await api.POST('/admin/bookings/{id}/resend-receipt', { params: path })
          : what === 'checkin'
            ? await api.POST('/admin/bookings/{id}/checkin', { params: path, body: { reason } })
            : await api.POST('/admin/bookings/{id}/checkout', { params: path, body: { reason } });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError,
  });

  const data = booking.data;
  if (!data)
    return <Panel label={t('admin.bookings.panelLabel')} title="—" onClose={onClose} children={null} />;

  const gross = data.grossCents ?? 0;
  const isToday = data.day === todayBerlin();
  // § 9.2 — a desk or room booking that still lies ahead can be moved.
  const rebookable =
    (data.kind === 'desk' || data.kind === 'room') &&
    data.day >= todayBerlin() &&
    !data.checkedInAt &&
    ['reserved', 'paid'].includes(data.status);
  const reasonOk = reason.trim().length >= 5;
  // § 9.2 — a free member booking has nothing to refund, so only "Stornieren".
  const hasMoney = gross > 0;
  const overLimit = (value: number) => role !== 'owner' && value > DEFAULT_STAFF_LIMIT_CENTS;

  const amountCents = mode === 'partial' ? Math.round(Number(amount.replace(',', '.')) * 100) : gross;
  const disabledMode = mode !== 'cancel_only' && overLimit(amountCents);

  return (
    <Panel
      label={t('admin.bookings.panelLabel', { number: data.number ?? '' })}
      title={objectLabel(data, t)}
      subtitle={`${formatDayShort(data.day)} · ${data.planName ?? '—'} · ${whoLabel(data, t)}`}
      onClose={onClose}
    >
      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      <div>
        <PanelRow
          label={t('admin.bookings.bookedAt')}
          value={`${formatDate(data.createdAt)}, ${formatTime(data.createdAt)} · ${t(
            `admin.bookings.source.${data.source}`,
          )}`}
        />
        <PanelRow
          label={t('admin.bookings.column.amount')}
          value={
            data.grossCents === null
              ? t('admin.bookings.included')
              : t('admin.bookings.grossInclVat', { amount: formatMoney(data.grossCents) })
          }
        />
        <PanelRow
          label={t('admin.bookings.invoice')}
          value={
            data.invoiceId && data.invoiceNumber ? (
              <Link to={`/rechnungen/${data.invoiceId}`}>
                {data.invoiceNumber} · {t(`admin.invoices.state.${data.invoiceStatus ?? 'open'}`)}
              </Link>
            ) : (
              '—'
            )
          }
        />
        <PanelRow
          label={t('admin.bookings.checkIn')}
          value={data.checkedInAt ? formatTime(data.checkedInAt) : '—'}
        />
        <PanelRow
          label={t('admin.bookings.checkOut')}
          value={
            data.checkedOutAt
              ? `${formatTime(data.checkedOutAt)}${
                  data.checkoutMode ? ` · ${t(`admin.bookings.checkoutMode.${data.checkoutMode}`)}` : ''
                }`
              : '—'
          }
        />
        {/* § 9.2 — "keine", or the draft whose publication would move this
            booking, with the way to go and resolve it. */}
        <PanelRow
          label={t('admin.bookings.conflicts')}
          value={
            data.publishConflict ? (
              <Link to="/grundrisse" className="text-teal-dark underline">
                {t(`admin.bookings.conflict.${data.publishConflict.reason}`)}
              </Link>
            ) : (
              t('admin.bookings.noConflicts')
            )
          }
        />
      </div>

      <div className="flex items-center gap-2">
        <Chip tone={STATUS_TONE[data.status] ?? 'neutral'} label={t(`admin.bookings.state.${data.status}`)} />
        {data.presenceUnverified ? <Chip tone="warn" label={t('admin.bookings.unverified')} /> : null}
      </div>

      {data.status !== 'cancelled' ? (
        <div className="bg-fog rounded-input flex flex-col gap-2 px-3.5 py-3">
          <p className="text-adminBody text-ink font-semibold">{t('admin.bookings.refundTitle')}</p>

          <div className="flex flex-wrap gap-1.5">
            {MODES.filter((value) => hasMoney || value === 'cancel_only').map((value) => (
              <Chip
                key={value}
                selected={value === mode}
                onPress={() => setMode(value)}
                label={
                  value === 'refund_card'
                    ? t('admin.bookings.mode.refund_card', { amount: formatMoney(gross) })
                    : t(`admin.bookings.mode.${value}`)
                }
              />
            ))}
          </div>

          {mode === 'partial' ? (
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode="decimal"
              placeholder={t('admin.bookings.amountPlaceholder')}
              className="border-line-strong rounded-input text-adminBody h-10 border px-3"
            />
          ) : null}

          <p className="text-adminSmall text-muted">{t(`admin.bookings.modeHint.${mode}`)}</p>

          {disabledMode ? (
            <p className="text-adminSmall text-warn-text">
              {t('admin.bookings.overLimit', { limit: formatMoney(DEFAULT_STAFF_LIMIT_CENTS) })}
            </p>
          ) : null}

          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('admin.bookings.reasonPlaceholder')}
            className="border-line-strong rounded-input text-adminBody h-14 border px-3 py-2"
          />

          <Button
            variant="danger"
            size="sm"
            disabled={!reasonOk || disabledMode}
            loading={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {mode === 'cancel_only'
              ? t('admin.bookings.cta.cancel_only')
              : mode === 'credit'
                ? t('admin.bookings.cta.credit', { amount: formatMoney(amountCents) })
                : t('admin.bookings.cta.refund', { amount: formatMoney(amountCents) })}
          </Button>
          <p className="text-adminSmall text-muted">{t('admin.bookings.withdrawalHint')}</p>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        {/* § 9.2 — overrides of a booking's check-in. A guest pass is not a
            booking: the guest checks in on the web page (BR-GUEST-03), and
            the override endpoints know only bookings. */}
        {isToday && !data.checkedInAt && data.kind !== 'guest' ? (
          <Button size="sm" variant="secondary" disabled={!reasonOk} onClick={() => act.mutate('checkin')}>
            {t('admin.bookings.checkInAction')}
          </Button>
        ) : null}
        {isToday && data.checkedInAt && !data.checkedOutAt && data.kind !== 'guest' ? (
          <Button size="sm" variant="secondary" disabled={!reasonOk} onClick={() => act.mutate('checkout')}>
            {t('admin.bookings.checkOutAction')}
          </Button>
        ) : null}
        {rebookable ? (
          <Button size="sm" variant="secondary" onClick={() => setRebooking(true)}>
            {t('admin.bookings.rebook')}
          </Button>
        ) : null}
        {data.userId ? (
          <Link
            to={`/mitglieder/${data.userId}`}
            className="border-line-strong text-ink font-body rounded-buttonSm inline-flex h-9 items-center justify-center border bg-white text-sm font-semibold"
          >
            {t('admin.bookings.openMember')}
          </Link>
        ) : null}
        <Button size="sm" variant="secondary" onClick={() => act.mutate('resend-receipt')}>
          {t('admin.bookings.resendReceipt')}
        </Button>
        {/* § 9.2 — a note goes into the same timeline as the actions, which is
            why it reuses the reason field rather than opening a dialog. */}
        <Button
          size="sm"
          variant="secondary"
          disabled={reason.trim().length === 0}
          onClick={() => note.mutate()}
        >
          {t('admin.bookings.note')}
        </Button>
      </div>

      {rebooking && (data.kind === 'desk' || data.kind === 'room') ? (
        <RebookDialog
          bookingId={id}
          kind={data.kind}
          locationId={data.locationId}
          day={data.day}
          blockStart={data.blockStart}
          currentResourceId={data.resourceId}
          onClose={() => setRebooking(false)}
        />
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-1.5">
        <p className="text-label text-muted uppercase">{t('admin.bookings.history')}</p>
        <ul className="text-adminSmall text-muted flex flex-col gap-1">
          {data.history.map((entry, index) => (
            <li key={`${entry.at}-${index}`}>
              {formatDate(entry.at)} {formatTime(entry.at)} · {t(`admin.bookings.event.${entry.event}`)}
              {entry.actor ? ` · ${entry.actor}` : ''}
              {entry.reason ? ` — ${entry.reason}` : ''}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
